/**
 * Changing or deleting one of the executive's own initiatives — build 6B.
 *
 * The improvement desk could add an initiative and never take one back or
 * correct it. The platform's own suggestions are computed and are not edited
 * here; only the entered ones are. The desk's note keeps every version.
 */
import { useState } from "react";
import { Button, Field, Input, Select } from "../../design/components";
import {
  INITIATIVE_STATUSES,
  initiativeRefusal,
  removeInitiative,
  updateInitiative,
  type EnteredInitiative,
} from "../../domain/executiveEntries";
import type { ImprovementEntered } from "../../domain/improvement";
import { t } from "../../i18n";
import s from "./EntryForms.module.css";

const num = (v: string) => (v.trim() === "" ? undefined : Number(v));

export function InitiativeEditForm({
  entered,
  item,
  onSave,
  onDone,
}: {
  entered: ImprovementEntered;
  item: EnteredInitiative;
  onSave: (next: ImprovementEntered) => Promise<void> | void;
  onDone: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [area, setArea] = useState(item.area);
  const [value, setValue] = useState(item.valuePerYear != null ? String(item.valuePerYear) : "");
  const [cost, setCost] = useState(item.cost != null ? String(item.cost) : "");
  const [owner, setOwner] = useState(item.owner ?? "");
  const [status, setStatus] = useState(item.status ?? "planned");
  const [why, setWhy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);

  const save = async () => {
    const patch = {
      name: name.trim(),
      area: area.trim(),
      valuePerYear: num(value),
      cost: num(cost),
      owner: owner.trim() || undefined,
      status,
    };
    const refused = initiativeRefusal(patch);
    if (refused) return setWhy(refused);
    setWhy(null);
    onDone();
    await onSave(updateInitiative(entered, item.key, patch));
  };

  return (
    <div className={s.form} data-testid={`initiative-form-${item.key}`}>
      <Field label="Name">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Area">
        <Input value={area} onChange={(e) => setArea(e.target.value)} />
      </Field>
      <Field label="Value a year">
        <Input type="number" min={0} step="any" value={value} onChange={(e) => setValue(e.target.value)} />
      </Field>
      <Field label="Cost">
        <Input type="number" min={0} step="any" value={cost} onChange={(e) => setCost(e.target.value)} />
      </Field>
      <Field label="Owner">
        <Input value={owner} onChange={(e) => setOwner(e.target.value)} />
      </Field>
      <Field label="Status">
        <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          {INITIATIVE_STATUSES.map((x) => (
            <option key={x} value={x}>
              {t(x)}
            </option>
          ))}
        </Select>
      </Field>
      {why && (
        <p className={`${s.err} ${s.wide}`} role="alert">
          {t(why)}
        </p>
      )}
      <div className={`${s.actions} ${s.wide}`}>
        <Button variant="primary" onClick={() => void save()}>
          {t("Save")}
        </Button>
        {confirm ? (
          <>
            <span className={s.muted}>{t("Delete this initiative?")}</span>
            <Button
              variant="danger"
              onClick={async () => {
                onDone();
                await onSave(removeInitiative(entered, item.key));
              }}
            >
              {t("Yes, delete it")}
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={() => setConfirm(true)}>
            {t("Delete")}
          </Button>
        )}
        <Button variant="ghost" onClick={onDone}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}
